import {
  Coverage,
  InsuranceDetailsView,
  InsuranceLimitedDetailsView,
} from 'Api/insurance';
import { LabelValuePair } from 'Components/InsuranceDetailsSections/insuranceDetailsSections.types';
import {
  BOAT_COVERAGES,
  CABIN_COVERAGES,
  CAR_COVERAGES,
  CARAVAN_COVERAGES,
  HOUSE_COVERAGES,
  MOTORCYCLE_COVERAGES,
} from 'Constants/coverageProduct.constants';
import { ProductName } from 'Constants/products.constants';
import { RentalType } from 'Forms/ChangeForm/products/car/changeCarForm.types';
import {
  OwnContributionCode,
  OwnContributionValue,
} from 'Forms/ChangeForm/products/car/components/OwnContribution/ownContribution.constants';
import {
  BoatCoverageProduct,
  CabinCoverageProduct,
  CaravanCoverageProduct,
  CarCoverage,
  CarCoverageProduct,
  HouseCoverageProduct,
  MotorcycleCoverageProduct,
} from 'Types/coverages/coverages.types';
import { isDefined, isInsuranceDetailsViewType } from 'Utils/type.utils';

export const mapOwnContributionCodeToOwnContributionValue = (
  ownContributionCode: OwnContributionCode,
): OwnContributionValue | undefined => {
  switch (ownContributionCode) {
    case OwnContributionCode.ONE_DEPRECATED:
      return OwnContributionValue.FOUR_THOUSAND;
    case OwnContributionCode.FIVE_DEPRECATED:
      return OwnContributionValue.SIX_THOUSAND;
    case OwnContributionCode.TWO_DEPRECATED:
      return OwnContributionValue.EIGHT_THOUSAND;
    case OwnContributionCode.FOUR_DEPRECATED:
      return OwnContributionValue.THIRTY_THOUSAND;
    case OwnContributionCode.SIX_DEPRECATED:
      return OwnContributionValue.TWELVE_THOUSAND;
    case OwnContributionCode.THREE_DEPRECATED:
      return OwnContributionValue.SIXTEEN_THOUSAND;
    case OwnContributionCode.ELEVEN:
      return OwnContributionValue.SIX_THOUSAND;
    case OwnContributionCode.TWELVE:
      return OwnContributionValue.EIGHT_THOUSAND;
    case OwnContributionCode.THIRTEEN:
      return OwnContributionValue.TEN_THOUSAND;
    case OwnContributionCode.FIFTEEN:
      return OwnContributionValue.FOURTEEN_THOUSAND;
    case OwnContributionCode.SEVENTEEN:
      return OwnContributionValue.EIGHTEEN_THOUSAND;
    case OwnContributionCode.NINETEEN:
      return OwnContributionValue.THIRTY_TWO_THOUSAND;
    default:
      return undefined;
  }
};

export const updateOwnRiskValues = (coverages: Coverage[]): Coverage[] =>
  coverages.map((coverage) => {
    const AFFECTED_COVERAGES = [
      ...BOAT_COVERAGES,
      ...CABIN_COVERAGES,
      ...CAR_COVERAGES,
      ...CARAVAN_COVERAGES,
      ...HOUSE_COVERAGES,
      ...MOTORCYCLE_COVERAGES,
    ];

    const shouldUpdate =
      AFFECTED_COVERAGES.includes(
        coverage.coverageProduct as
          | BoatCoverageProduct
          | CabinCoverageProduct
          | CarCoverageProduct
          | CaravanCoverageProduct
          | HouseCoverageProduct
          | MotorcycleCoverageProduct,
      ) && !isDefined(coverage.ownRisk.value);

    if (shouldUpdate) {
      const ownRiskCode = coverage.ownRisk.code as OwnContributionCode;
      const mappedValue =
        mapOwnContributionCodeToOwnContributionValue(ownRiskCode);

      return {
        ...coverage,
        ownRisk: {
          ...coverage.ownRisk,
          value: mappedValue,
        },
      };
    }
    return coverage;
  });

export const getInsuranceViewContent = (
  details: InsuranceDetailsView | InsuranceLimitedDetailsView,
) => {
  const { title, primary, secondary } = filterUndefinedProperties(details);
  const mappedSecondaryContent = getMappedSecondaryContent(details, secondary);

  return {
    title,
    primary,
    secondary: mappedSecondaryContent,
  };
};

const filterUndefinedProperties = (
  details: InsuranceDetailsView | InsuranceLimitedDetailsView,
) => {
  return {
    title: details.title.filter((e) => e !== null),
    primary: details.primary.filter((e) => e.value !== null),
    secondary: details.secondary?.filter((detail) => detail.value !== null),
  };
};

const getMappedSecondaryContent = (
  details: InsuranceDetailsView | InsuranceLimitedDetailsView,
  secondary: LabelValuePair[] | undefined,
): LabelValuePair[] | undefined => {
  if (!isInsuranceDetailsViewType(details)) {
    return secondary;
  }
  const { insuranceProduct, rentalType, coverage } = details;

  switch (insuranceProduct) {
    case ProductName.CAR:
      return getCarSecondaryDetails(secondary, rentalType, coverage);
    default:
      return secondary;
  }
};

const getCarSecondaryDetails = (
  secondary: LabelValuePair[] | undefined,
  rentalType: RentalType | undefined,
  coverage: string,
) => {
  return secondary?.map((detail) => {
    if (detail.label === 'Leiebil ved skade') {
      return {
        ...detail,
        value: getRentalTypeDetailValue(rentalType, coverage),
      };
    }
    return detail;
  });
};

const getRentalTypeDetailValue = (
  rentalType: RentalType | undefined,
  coverage: string,
) => {
  if (
    rentalType === RentalType.RENTAL_TYPE_EXTENDED ||
    coverage === CarCoverage.FULL_SUPER
  ) {
    return 'Ja, utvidet';
  }
  if (rentalType === RentalType.RENTAL_TYPE_ORDINARY) {
    return 'Ja';
  }
  return 'Nei';
};
